import React, { useState, useEffect } from 'react';
import { appConfirmMessage } from '../../../utils/appConfirm';
import { FaCommentDots, FaEdit, FaTrash } from 'react-icons/fa';
import CreateNote from '../../CreateNote';
import { graphqlRequest } from '../../../utils/graphqlRequest';
import { toast } from 'react-toastify';
import "../styles/CleanerTimeCard.css";

export default function CleanerTimeCard({ members = [], selectedUserId, selectedDate, refreshKey = 0 }) {
  const [showNote, setShowNote] = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    const fetchTimeCards = async () => {
      if (!selectedUserId) {
        setRecords([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        // Format date for API query (YYYY-MM-DD)
        const formattedDate = selectedDate.toISOString().split('T')[0];
        
        // Fetch time cards from backend API
        const query = `
          query FindTimeCards($userId: String!, $date: String!) {
            findTimeCards(userId: $userId, date: $date) {
              id
              userId
              date
              timeIn
              timeOut
              duration
              notes
              user {
                id
                firstName
                lastName
              }
            }
          }
        `;
        
        const data = await graphqlRequest(query, { 
          userId: selectedUserId, 
          date: formattedDate 
        });
        
        const timeCards = data?.findTimeCards || [];
        const selectedMember = members.find(m => m.id === selectedUserId);
        
        // Format records for display
        const formattedRecords = timeCards.map((timeCard) => ({
          id: timeCard.id,
          userId: timeCard.userId,
          name: selectedMember?.name || `${timeCard.user?.firstName || ''} ${timeCard.user?.lastName || ''}`.trim(),
          date: timeCard.date,
          timeIn: timeCard.timeIn,
          timeOut: timeCard.timeOut,
          duration: timeCard.duration || '0 hours',
          notes: timeCard.notes,
        }));
        
        setRecords(formattedRecords);
      } catch (err) {
        console.error('Error fetching time cards:', err);
        toast.error('Failed to fetch time card records');
        setRecords([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTimeCards();
  }, [selectedUserId, selectedDate, members, refreshKey]);

  const handleDelete = async (id) => {
    if (!await appConfirmMessage('Are you sure you want to delete this time card entry?')) {
      return;
    }

    try {
      const mutation = `
        mutation RemoveTimeCard($id: String!) {
          removeTimeCard(id: $id) {
            id
          }
        }
      `;

      await graphqlRequest(mutation, { id });
      setRecords(records.filter((rec) => rec.id !== id));
      toast.success('Time card entry deleted successfully');
    } catch (err) {
      console.error('Error deleting time card:', err);
      const errorMessage = err?.message || 'Failed to delete time card entry';
      toast.error(errorMessage);
    }
  };

  // Calculate duration from timeIn and timeOut
  const calculateDuration = (timeIn, timeOut) => {
    if (!timeIn || !timeOut) return '0 hours';

    try {
      const parseTime = (timeStr) => {
        if (!timeStr) return null;
        
        let time = timeStr.trim().toUpperCase();
        const isPM = time.includes('PM');
        const isAM = time.includes('AM');
        time = time.replace(/[AP]M/gi, '').trim();
        
        const [hours, minutes] = time.split(':').map(Number);
        if (isNaN(hours) || isNaN(minutes)) return null;
        
        let hour24 = hours;
        if (isPM && hours !== 12) hour24 = hours + 12;
        if (isAM && hours === 12) hour24 = 0;
        
        return hour24 * 60 + minutes;
      };

      const timeInMinutes = parseTime(timeIn);
      const timeOutMinutes = parseTime(timeOut);

      if (timeInMinutes === null || timeOutMinutes === null) {
        return 'Invalid time';
      }

      let diffMinutes = timeOutMinutes - timeInMinutes;
      if (diffMinutes < 0) {
        diffMinutes += 24 * 60;
      }

      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;

      if (minutes === 0) {
        return `${hours} hour${hours !== 1 ? 's' : ''}`;
      } else if (hours === 0) {
        return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
      } else {
        return `${hours} hour${hours !== 1 ? 's' : ''} ${minutes} minute${minutes !== 1 ? 's' : ''}`;
      }
    } catch (error) {
      console.error('Error calculating duration:', error);
      return 'Invalid time';
    }
  };

  const handleEdit = (record) => {
    setEditingId(record.id);
    setEditData({ ...record });
  };

  const handleTimeChange = (field, value) => {
    const updatedData = { ...editData, [field]: value };
    
    if ((field === 'timeIn' || field === 'timeOut') && updatedData.timeIn && updatedData.timeOut) {
      updatedData.duration = calculateDuration(updatedData.timeIn, updatedData.timeOut);
    }
    
    setEditData(updatedData);
  };

  const handleSave = async () => {
    try {
      const finalDuration = calculateDuration(editData.timeIn, editData.timeOut);
      
      const mutation = `
        mutation UpdateTimeCard($input: UpdateTimeCardInput!) {
          updateTimeCard(updateTimeCardInput: $input) {
            id
            userId
            date
            timeIn
            timeOut
            duration
            notes
          }
        }
      `;

      const variables = {
        input: {
          id: editingId,
          date: editData.date,
          timeIn: editData.timeIn,
          timeOut: editData.timeOut,
          duration: finalDuration,
          notes: editData.notes,
        },
      };

      await graphqlRequest(mutation, variables);
      
      // Refresh data after update
      const formattedDate = selectedDate.toISOString().split('T')[0];
      const query = `
        query FindTimeCards($userId: String!, $date: String!) {
          findTimeCards(userId: $userId, date: $date) {
            id
            userId
            date
            timeIn
            timeOut
            duration
            notes
            user {
              id
              firstName
              lastName
            }
          }
        }
      `;
      
      const data = await graphqlRequest(query, { 
        userId: selectedUserId, 
        date: formattedDate 
      });

      const timeCards = data?.findTimeCards || [];
      const selectedMember = members.find(m => m.id === selectedUserId);
      
      const formattedRecords = timeCards.map((timeCard) => ({
        id: timeCard.id,
        userId: timeCard.userId,
        name: selectedMember?.name || `${timeCard.user?.firstName || ''} ${timeCard.user?.lastName || ''}`.trim(),
        date: timeCard.date,
        timeIn: timeCard.timeIn,
        timeOut: timeCard.timeOut,
        duration: timeCard.duration || '0 hours',
        notes: timeCard.notes,
      }));
      
      setRecords(formattedRecords);
      setEditingId(null);
      toast.success('Time card entry updated successfully');
    } catch (err) {
      console.error('Error updating time card:', err);
      const errorMessage = err?.message || 'Failed to update time card entry';
      toast.error(errorMessage);
    }
  };

  // Get employee ID from members list (matching Team Member page format)
  const getEmployeeId = (userId, members) => {
    const member = members.find(m => m.id === userId);
    return member?.employeeId || userId?.substring(0, 8).toUpperCase() || 'N/A';
  };

  return (
    <div className="timecard-cleaner-table-container">
      <table className="timecard-cleaner-table">
        <thead>
          <tr>
            <th>Employee id</th>
            <th>Name</th>
            <th>Date</th>
            <th>Time In</th>
            <th>Time Out</th>
            <th>Duration</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                Loading time card records...
              </td>
            </tr>
          ) : records.length === 0 ? (
            <tr>
              <td colSpan="7" style={{ textAlign: 'center', padding: '20px' }}>
                No time card records found for this date
              </td>
            </tr>
          ) : (
            records.map((rec, index) => (
              editingId === rec.id ? (
                <tr key={rec.id}>
                  <td>{getEmployeeId(rec.userId, members)}</td>
                  <td>{rec.name}</td>
                  <td>
                    <input
                      type="date"
                      value={editData.date || ''}
                      onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      value={editData.timeIn || ''}
                      onChange={(e) => handleTimeChange('timeIn', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="time"
                      value={editData.timeOut || ''}
                      onChange={(e) => handleTimeChange('timeOut', e.target.value)}
                    />
                  </td>
                  <td>{editData.duration || calculateDuration(editData.timeIn, editData.timeOut)}</td>
                  <td className="timecard-cleaner-actions">
                    <button onClick={handleSave} style={{ marginRight: '5px' }}>Save</button>
                    <button onClick={() => setEditingId(null)}>Cancel</button>
                  </td>
                </tr>
              ) : (
            <tr key={rec.id}>
                  <td>{getEmployeeId(rec.userId, members)}</td>
              <td>{rec.name}</td>
              <td>{rec.date}</td>
              <td>{rec.timeIn}</td>
              <td>{rec.timeOut}</td>
              <td>{rec.duration}</td>
              <td className="timecard-cleaner-actions">
                <FaCommentDots
                  className="icon comment"
                  onClick={() => setShowNote(true)}
                />
                    <FaEdit 
                      className="icon edit" 
                      onClick={() => handleEdit(rec)}
                    />
                <FaTrash
                  className="icon delete"
                  onClick={() => handleDelete(rec.id)}
                />
              </td>
            </tr>
              )
            ))
          )}
        </tbody>
      </table>

      {showNote && <CreateNote onClose={() => setShowNote(false)} />}
    </div>
  );
}
